import { reQuote } from '@/api/eiqAPI';
import bikeCc from '@/core/bikeCc';
import {
  carPacketOptions,
  customerTypeOptions,
  getLicenseYearsOptions,
  jobOptions,
  vehicleTypeOptions
} from '@/core/formOptions';
import {
  calculateAge,
  mapFuel,
  parseNationalityValue,
  transformPacketTypeToLiteral
} from '@/core/helpers';
import postalCodes from '@/core/postalCodes';
import taxableHPs from '@/core/taxableHPs';
import { eiqMappingTranslations } from "@/core/textConstants";
import { VehiclesModel } from '@/core/vehicleModel';
import { VehicleTypes, VehicleTypeToEiqKey } from '@/enums/filters';
import { LocalStorageService } from "./localStorageService";

export const QuoteService = () => {
    const BASE_QUOTE_KEY = 'quoteFilters';

    const OTHER_MODEL_LABEL = "Άλλο μοντέλο";
    const OTHER_MANUFACTURER_CODE = 99999999999;

    const get = () => {
        let storedData = LocalStorageService.get(BASE_QUOTE_KEY) ?? {}
        return storedData
    }

    const store = (data) => {
        const existingData = LocalStorageService.get(BASE_QUOTE_KEY) ?? {};

        const dataToStore = {
            ...existingData,
            ...data,
        };

        LocalStorageService.set(BASE_QUOTE_KEY, dataToStore)
    }

    const getEiqLabelFromVehicleType = (type) => {
      const key = VehicleTypeToEiqKey[type];
      return eiqMappingTranslations[key] ?? '';
    };

    const parseVehicle = (data) => {
        const quote = get();
        const petrolFuel = mapFuel("Βενζινοκινητήρας");
        const electricFuel = mapFuel("Ηλεκτροκινητήρας");

        const isCarCategory = quote.vehicleType === VehicleTypes.Car;
        const isBikeCategory = quote.vehicleType === VehicleTypes.Bike;
        const isTruckCategory = quote.vehicleType === VehicleTypes.Truck;
        const isRuralCategory = quote.vehicleType === VehicleTypes.Rural;
        const isTaxiCategory = quote.vehicleType === VehicleTypes.Taxi;
        const isRentalCarCategory = quote.vehicleType === VehicleTypes.RentalCar;
        const isCarRelatedCategory = [isCarCategory, isTaxiCategory, isRentalCarCategory].some(Boolean);

        // Precompute helper booleans
        const isOtherManufacturer = data.vehicle.manufacturer === OTHER_MANUFACTURER_CODE;
        const isOtherModel = data.vehicle.model === OTHER_MANUFACTURER_CODE;
        const isNonCarOtherModel = isOtherModel && !isCarRelatedCategory;

        // Precompute common values
        const manufacturerText = isOtherManufacturer ? "Άλλη Μάρκα" : data.vehicle.manufacturer;
        const vehicleModel = isNonCarOtherModel ? OTHER_MODEL_LABEL : data.vehicle.model;
        
        const vehicleModelEditionId = isNonCarOtherModel
            ? OTHER_MODEL_LABEL
            : (isTruckCategory || isRuralCategory)
                ? data.vehicle?.vehicleModelEdition
                : String(data.vehicle.model);
        
        const vehicleModelEdition = (data.vehicle?.vehicleModelEdition === OTHER_MANUFACTURER_CODE && !isCarRelatedCategory)
            ? OTHER_MODEL_LABEL
            : (isTruckCategory || isRuralCategory)
                ? data.vehicle.model
                : data.vehicle?.vehicleModelEdition;
        
        const vehicleModelText = isCarRelatedCategory
            ? data.vehicle.vehicleModelText
            : isOtherModel
                ? OTHER_MODEL_LABEL
                : data.vehicle.vehicleModelText ?? data.vehicle.model;

        let manufacturerDate = `${data.vehicle.manufactureYear}-${data.vehicle.manufactureMonth.padStart(2, '0')}-01T14:13:11.514Z`

        let transferDate;
        if (!data.transferDate?.year && !data.transferDate?.month) {
          transferDate = manufacturerDate
        } else {
          transferDate = `${data.transferDate?.year}-${data.transferDate?.month.padStart(2, '0')}-01T14:13:11.514Z`
        }

        let value = (!data.vehicle.value ? 3000 : data.vehicle.value);

        let vehicle =  {
          license_plate: data.licensePlate,
          vehicle_type: data.vehicleType,
          isHeavy: data.vehicle?.isHeavy ?? false,
          hasAlarm: data.vehicle?.hasAlarm ?? false,
          hasFridge: data.vehicle?.hasFridge ?? false,
          manufacturer_text: manufacturerText,
          color: 0,
          value,
          vehicle_model: vehicleModel,
          vehicle_model_edition_id: vehicleModelEditionId,
          vehicle_model_edition: vehicleModelEdition,
          vehicle_model_text: vehicleModelText,
          vehicleModelExtraEditionText: data.vehicle.vehicleModelExtraEditionText,
          taxable_hp: parseInt(data.vehicle.taxableHP ?? 0, 10),
          manufacturing_date: manufacturerDate,
          age: calculateAge(new Date(`${data.vehicle.manufactureYear}-${data.vehicle.manufactureMonth}-01T00:00:00Z`)),
          vehicle_transfer_date: transferDate,
          seats: data.vehicle.seats,
          cc: parseInt(data.vehicle.cc, 10),
          is_vehicle_used: data.usedVehicle,
        };

        if ( data.vehicle.manufacturerId ) {
          vehicle.manufacturer_id = String(data.vehicle.manufacturerId);
        }

        if (isCarRelatedCategory || isBikeCategory) 
          vehicle = {
            ...vehicle, 
            ...{
              roofType: 0,
              frame_number: "",
              kw: data.vehicle.kw,
              model_id: data.vehicle.modelId,
              emissions: data.vehicle.emissions,
              acceleration: data.vehicle.acceleration,
              combinedHorsepower: data.vehicle.combinedHorsepower,
              fuel: isBikeCategory
                  ? data.vehicle.isElectric
                      ? electricFuel
                      : petrolFuel
                  : data.vehicle.fuel,
            }
          }

        if (isBikeCategory && data.vehicle.isElectric)
            vehicle.fuel = electricFuel
        else if ( isBikeCategory && data.vehicle.isElectric === false ) 
            vehicle.fuel = petrolFuel

        if (isTruckCategory || isRuralCategory) 
          vehicle = {
            ...vehicle, 
            ...{
              trailer: 0,
              manufacturer_id: data.vehicle.manufacturer,
              fuel: data.vehicle.fuel,
            }
          }

        return vehicle
    }

    const parseContext = (data, checkoutData, discountCode) => {
        const quote = get();
        let packetType = transformPacketTypeToLiteral(data.packet)

        return {
          "classification": {
            "category": "insurance",
            "type": quote.vehicleType,
            "filters": {
              "packetType": packetType,
            }
          },
          "step": discountCode ? 3 : 0,
          "quoteUser": {
            "offerUser": "",
            "username": "insurancemarket"
          },
          "crmUserBelongsToIM": true,
          "environment": {
            "source": "site"
          }
        }
    }

    const parseContract = (data, startingDate) => {
        const today = new Date();
        let startDate = new Date();

        if (startingDate) {
          const parsedDate = new Date(startingDate);
          parsedDate.setMinutes(parsedDate.getMinutes() - parsedDate.getTimezoneOffset());
          
          const parsedMidnight = new Date(parsedDate);
          parsedMidnight.setHours(0, 0, 0, 0);

          today.setHours(0, 0, 0, 0);

          const isValidDate = parsedDate instanceof Date && !isNaN(parsedDate);
          const isTodayOrFuture = parsedMidnight >= today;

          if (isValidDate && isTodayOrFuture) {
            startDate = parsedDate;
          }
        }

        return {
          "packet_category": data.packet,
          "duration": data?.duration ?? 12,
          "start_date": startDate.toISOString(),
          "contractMethod": ""
        }
    }
    
    const parseViewData = async (data) => {
        const quote = get();
        const electricFuel = mapFuel("Ηλεκτροκινητήρας");

        const isCarCategory = quote.vehicleType === VehicleTypes.Car;
        const isBikeCategory = quote.vehicleType === VehicleTypes.Bike;
        const isTruckCategory = quote.vehicleType === VehicleTypes.Truck;
        const isRuralCategory = quote.vehicleType === VehicleTypes.Rural;
        const isTaxiCategory = quote.vehicleType === VehicleTypes.Taxi;
        const isRentalCarCategory = quote.vehicleType === VehicleTypes.RentalCar;

        let trailerText = vehicleTypeOptions.find(item => item.value === data.vehicleType)?.label ?? "";
        let licenseYearsLabel = getLicenseYearsOptions(data.birthDate).find(item => item.value === data.licenseYears).label;

        let viewData = {
          "vehicleCategory": getEiqLabelFromVehicleType(quote.vehicleType),
          "vehicleEdition": data.vehicle.extraEdition ?? "Άλλη έκδοση",
          "licenseYears": licenseYearsLabel,
          "postalCode": postalCodes.find(item => item.value === data.postalCode).label,
          "minRecommendedValue": data.vehicle.minRecommendedValue,
          "maxRecommendedValue": data.vehicle.maxRecommendedValue,
          "trailer_text": trailerText
        }

        if (isCarCategory || isTruckCategory || isRuralCategory || isTaxiCategory || isRentalCarCategory) {
          if (data.vehicle.fuel === electricFuel) {
            viewData.kw = data.vehicle.kw
          } else {
            let taxableHpLabel = taxableHPs.find(item => item.id === data.vehicle.taxableHP).label
            viewData.taxHp = taxableHpLabel
          }
        } else if (isBikeCategory) {
          if (data.vehicle.fuel === electricFuel) {
            viewData.kw = data.vehicle.kw
          } else {
            viewData.taxHp = bikeCc.find(option => option.id === data.vehicle.cc)?.label;
          }
        }

        if (data.secondDriver) {
          let licenseYears2Label = getLicenseYearsOptions(data.birthDate2).find(item => item.value === data.licenseYears2).label
          viewData.licenseYears2 = licenseYears2Label
        }

        if(data.customerType === 4) {
          viewData.job = jobOptions.find(item => item.value === data.job).label;
          viewData.customerType = customerTypeOptions.find(item => item.value === data.customerType).label;
        }

        return viewData
    }

    const parseCustomer = (data, checkoutData, discountCode) => {
        let birhDate = new Date(`${data.birthDate.year}-${data.birthDate.month}-${data.birthDate.day}T00:00:00Z`);
        let age = calculateAge(birhDate)
        let licenseIssueYear = new Date().getFullYear() - data.licenseYears
        let licenseIssueDate = `${licenseIssueYear}-01-01T00:00:00Z`

        let driverData =  [
          {
            "name": checkoutData?.name ?? null,
            "surname": checkoutData?.surname ?? null,
            "father_name": checkoutData?.father_name ?? null,
            "email": checkoutData?.email ?? null,
            "landline_num": checkoutData?.landline_num ?? null,
            "cellphone_num": checkoutData?.cellphone_num ?? null,
            "city": "ΑΘΗΝΑ",
            "address": checkoutData?.address ?? null,
            "address_no": checkoutData?.address_no ?? null,
            "licenseCategory": parseInt(checkoutData?.license_cat) ?? 0,
            "tax_no": checkoutData?.tax_no ?? null,
            "licenseCountry": -9999,
            "tax_office": checkoutData?.tax_office ?? null,
            "identityNumber": null,
            "date_of_birth": birhDate.toISOString(),
            "age": age,
            "license_years": data.licenseYears,
            "licenseIssueDate": licenseIssueDate,
            "isOwner": true,
            "nationality": parseNationalityValue(checkoutData?.nationality),
            "maritalStatus": 0
          },
        ]

        if ( data.secondDriver ) {
          let birhDate2 = new Date(`${data.birthDate2.year}-${data.birthDate2.month}-${data.birthDate2.day}T00:00:00Z`);
          let age2 = new Date().getFullYear() - birhDate2.getFullYear()
          let licenseIssueYear2 = new Date().getFullYear() - data.licenseYears2
          let licenseIssueDate2 = `${licenseIssueYear2}-01-01T00:00:00Z`

          driverData.push({
            "name": checkoutData?.name2 ?? null,
            "surname": checkoutData?.surname2 ?? null,
            "father_name": null,
            "email": null,
            "landline_num": null,
            "cellphone_num": null,
            "city": "ΑΘΗΝΑ",
            "address": null,
            "address_no": null,
            "tax_no": null,
            "tax_office": null,
            "identityNumber": null,
            "date_of_birth": birhDate2.toISOString(),
            "age": age2,
            "license_years": data.licenseYears2,
            "licenseIssueDate": licenseIssueDate2,
            "isOwner": false,
            "nationality": parseNationalityValue(checkoutData?.nationality2),
            "maritalStatus": 0
          })
        }

        let customerData =   {
          "customer_type": data.customerType,
          "occupation_type": data.job,
          "legal_entity_type": null,
          "driver_data": driverData,
          "postal_code": data.postalCode,
          "has_high_turnover": false,
          "only_driver": data?.only_driver ?? false,
          "county": 5,
          "currentContract": {
            "company": "",
            "endDate": ""
          }
        }

        if (data.customerType === 3) {
          customerData.has_high_turnover = data.has_high_turnover
        }

        if (discountCode && discountCode.length > 0) {
          customerData.discount_code = discountCode
        }
        
        return customerData
    }

    const parseQuoteData = (data, insuranceCovers) => {
      const quote = get();
      let packet = carPacketOptions.find(item => item.value === data.packet).value
      let packetLiteral = transformPacketTypeToLiteral(packet);

      let availableCovers = Object.keys(VehiclesModel[quote.vehicleType ][packetLiteral].availableFilters)
      let cardCovers = VehiclesModel[quote.vehicleType][packetLiteral].cardCovers
      let filters = [...availableCovers, ...cardCovers]
      let covers = insuranceCovers?.reduce((acc, key) => {
        if(!filters.includes(key)) {
          return acc;
        }
        acc[key] = { attributes: {} };
        return acc;
      }, {});

      return {
        "quoteOneCombination": true,
        "selectedFeatures": covers ?? {},
      }
    }

    const createQuoteRequest = async (formData, selectedFilters, trackingData, checkoutData, multiproductId, discountCode=null) => {
        const quote = get();

        let vehicle = parseVehicle(formData)
        let customer = parseCustomer(formData, checkoutData, discountCode)
        let context = parseContext(formData, checkoutData, discountCode)
        let viewData = await parseViewData(formData)
        let contract = parseContract(formData, quote.startingDate)
        let quoteData = parseQuoteData(formData, selectedFilters)

        const payload = {
          "formData": {
            "vehicle": vehicle,
            "customer": customer,
            "contract": contract,
            "documents": []
          },
          "context": context,
          "trackingData": trackingData,
          "viewData": viewData,
          "quoteData": quoteData
        }

        if (checkoutData) {
            payload.multiProductIds = [multiproductId]
        }

        return payload;
    }

    const reprice = async (req) => {
      const resp = await reQuote(req)
      const newPackage = Array.isArray(resp) ? resp[0] : null;
      
      let newPrice = 0;

      newPackage?.quotedProducts?.forEach(quotedProduct => {
        newPrice += parseFloat(quotedProduct.combinations[0].price);
      });

      return {
        newPrice,
        newPackage,
      };
    }

    return {
        get,
        store,
        createQuoteRequest,
        reprice,
    }
}
